// client/src/api/auth/index.ts - DEBUG VERSION
import { apiClient } from "../client";
import { ENDPOINTS } from "../endpoints";
import { AxiosError } from "axios";

/** Type-safe helper to extract error message from Axios errors */
const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof AxiosError) {
    return error.response?.data?.message || error.response?.data?.error || error.message || fallback;
  }
  if (error instanceof Error) {
    return error.message || fallback;
  }
  return fallback;
};

// ========== EMAIL VERIFICATION ==========

export interface VerifyEmailRequest {
  email: string;
  code: string;
}

export interface VerifyEmailResponse {
  success: boolean;
  message: string;
  token?: string;
  refreshToken?: string;
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    emailVerified: boolean;
  };
  redirectPath?: string;
  remainingAttempts?: number;
}

export const verifyEmail = async (data: VerifyEmailRequest): Promise<VerifyEmailResponse> => {
  try {
    // console.log("🔵 [verifyEmail] Request:", data);
    const response = await apiClient.post(ENDPOINTS.AUTH.VERIFY_EMAIL, data);
    // console.log("🟢 [verifyEmail] Response:", response.data);
    
    if (response.data.success && response.data.user) {
      localStorage.setItem("user", JSON.stringify(response.data.user));
    }
    
    return response.data;
  } catch (error: unknown) {
    console.error("🔴 [verifyEmail] Error:", error);
    if (error instanceof AxiosError) {
      console.error("🔴 [verifyEmail] Error Response:", error.response?.data);
    }
    throw new Error(getErrorMessage(error, "Verification failed"));
  }
};

export interface ResendVerificationRequest {
  email: string;
}

export interface ResendVerificationResponse {
  success: boolean;
  message: string;
  expiresIn?: string;
}

export const resendVerificationCode = async (
  data: ResendVerificationRequest
): Promise<ResendVerificationResponse> => {
  try {
    // console.log("🔵 [resendVerification] Request:", data);
    const response = await apiClient.post(ENDPOINTS.AUTH.RESEND_VERIFICATION, data);
    // console.log("🟢 [resendVerification] Response:", response.data);
    return response.data;
  } catch (error: unknown) {
    console.error("🔴 [resendVerification] Error:", error);
    if (error instanceof AxiosError) {
      console.error("🔴 [resendVerification] Error Response:", error.response?.data);
    }
    throw new Error(getErrorMessage(error, "Failed to resend verification code"));
  }
};

// ========== REGISTRATION ==========

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  password: string;
  role?: string;
  referralCode?: string;
}

export interface RegisterResponse {
  success: boolean;
  message?: string;
  needsVerification?: boolean;
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    emailVerified: boolean;
  };
  token?: string;
  refreshToken?: string;
  redirectPath?: string;
  expiresIn?: string;
}

export const register = async (data: RegisterRequest): Promise<RegisterResponse> => {
  try {
    // console.log("🔵 [register] Request:", data);
    // console.log("🔵 [register] Endpoint:", ENDPOINTS.AUTH.REGISTER);
    
    const response = await apiClient.post(ENDPOINTS.AUTH.REGISTER, data);
    
    // console.log("🟢 [register] Full Response:", response);
    // console.log("🟢 [register] Response Data:", response.data);
    // console.log("🟢 [register] Response Status:", response.status);
    
    if (response.data.success && !response.data.needsVerification && response.data.user) {
      console.log("✅ Registration successful");
      localStorage.setItem("user", JSON.stringify(response.data.user));
    } else if (response.data.needsVerification) {
      console.log("📧 User needs email verification");
    }
    
    return response.data;
  } catch (error: unknown) {
    console.error("🔴 [register] Error:", error);
    if (error instanceof AxiosError) {
      console.error("🔴 [register] Error Response:", error.response);
      console.error("🔴 [register] Error Data:", error.response?.data);
      console.error("🔴 [register] Error Status:", error.response?.status);
    }
    
    const errorMessage = getErrorMessage(error, "Registration failed");
    
    console.error("🔴 [register] Final Error Message:", errorMessage);
    throw new Error(errorMessage);
  }
};



export interface SendInvitationRequest {
  email: string;
  role: string;
}

export interface SendInvitationResponse {
  success: boolean;
  message: string;
  needsVerification?: boolean;
  token?: string;
  redirectPath?: string;
  data?: unknown;
}

export const sendInvitation = async (
  data: SendInvitationRequest
): Promise<SendInvitationResponse> => {
  try {
    const response = await apiClient.post<SendInvitationResponse>(
      ENDPOINTS.AUTH.INVITATION_SEND,
      data
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error, "Failed to send invitation"));
  }
};



// ========== PASSWORD MANAGEMENT ==========

export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  success: boolean;
  message?: string;
}

export const forgotPassword = async (
  data: ForgotPasswordRequest
): Promise<ForgotPasswordResponse> => {
  try {
    const response = await apiClient.post(ENDPOINTS.AUTH.FORGOT_PASSWORD, data);
    return {
      success: response.data.success,
      message: response.data.message || "Password reset code sent to your email",
    };
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error, "Failed to send reset code. Please try again."));
  }
};

export interface ResetPasswordRequest {
  /** 6-digit OTP code from email (preferred) */
  code?: string;
  /** Email associated with the code (used together with code) */
  email?: string;
  /** Legacy JWT token (kept for backward compatibility) */
  token?: string;
  password: string;
}

export interface ResetPasswordResponse {
  success: boolean;
  message?: string;
  error?: string;
  validationErrors?: string[];
}

export const resetPassword = async (
  data: ResetPasswordRequest
): Promise<ResetPasswordResponse> => {
  try {
    const response = await apiClient.post(ENDPOINTS.AUTH.RESET_PASSWORD, data);
    return {
      success: response.data.success,
      message: response.data.message || "Password reset successful",
      error: response.data.error,
      validationErrors: response.data.validationErrors,
    };
  } catch (error: unknown) {
    const responseData = error instanceof AxiosError ? error.response?.data : undefined;
    return {
      success: false,
      message: responseData?.message,
      error: responseData?.error || responseData?.message || "Failed to reset password. Your link may have expired.",
      validationErrors: responseData?.validationErrors,
    };
  }
};

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface ChangePasswordResponse {
  success: boolean;
  message?: string;
}

export const changePassword = async (
  data: ChangePasswordRequest
): Promise<ChangePasswordResponse> => {
  try {
    const response = await apiClient.put(ENDPOINTS.USER.PASSWORD, data);
    return {
      success: response.data.success,
      message: response.data.message || "Password changed successfully",
    };
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error, "Failed to change password. Please check your current password."));
  }
};

// ========== LOGOUT ==========

export const logout = () => {
  localStorage.removeItem("user");
  localStorage.removeItem("tempToken");
};

// ========== SOCIAL AUTH ==========

export interface SocialAuthRequest {
  token: string;
  provider: "google" | "facebook";
}

export const socialAuth = async (
  data: SocialAuthRequest
): Promise<Record<string, unknown>> => {
  try {
    const endpoint = data.provider === "google" 
      ? "/auth/social/google" 
      : "/auth/social/facebook";
      
    const response = await apiClient.post(endpoint, {
      token: data.token,
    });
    
    if (response.data.success && response.data.data) {
      const { user } = response.data.data;
      
      if (user) {
        localStorage.setItem("user", JSON.stringify(user));
      }
      
      return {
        success: true,
        user: user,
      };
    }
    
    return response.data;
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error, "Social login failed"));
  }
};
